import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, Send, XCircle, Clock, CheckCircle, Calendar, Upload, Plus, Trash2 } from 'lucide-react';
import { listSignatureRequests, sendSignatureRequest, cancelSignatureRequest, getSignatureEvents, uploadDocumentForSigning } from '../../services/esignatureService';
import { formatDateTime } from '../../utils/dateUtils';
import ContactPicker from '../common/ContactPicker';

const STATUS_COLORS = {
  draft: { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-500/30', icon: Clock },
  sent: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30', icon: Send },
  viewed: { bg: 'bg-cyan-500/20', text: 'text-cyan-300', border: 'border-cyan-500/30', icon: Eye },
  partially_signed: { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-500/30', icon: Clock },
  completed: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30', icon: CheckCircle },
  declined: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30', icon: XCircle },
  expired: { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-500/30', icon: Clock },
  cancelled: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30', icon: XCircle }
};

const SignatureRequestsList = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [events, setEvents] = useState([]);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadSigners, setUploadSigners] = useState([{ name: '', email: '', type: 'client' }]);
  const [uploadEmailSubject, setUploadEmailSubject] = useState('');
  const [uploadEmailMessage, setUploadEmailMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;

      const response = await listSignatureRequests(params);
      setRequests(response.data || []);
    } catch (err) {
      console.error('Error fetching signature requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (requestId) => {
    if (!window.confirm('Send this signature request to all signers?')) return;

    try {
      await sendSignatureRequest(requestId);
      alert('Signature request sent successfully!');
      fetchRequests();
    } catch (err) {
      console.error('Error sending request:', err);
      alert('Failed to send signature request');
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this signature request? This cannot be undone.')) return;

    try {
      await cancelSignatureRequest(requestId);
      alert('Signature request cancelled');
      fetchRequests();
    } catch (err) {
      console.error('Error cancelling request:', err);
      alert('Failed to cancel signature request');
    }
  };

  const handleViewEvents = async (request) => {
    try {
      const response = await getSignatureEvents(request.id);
      setEvents(response.data || []);
      setSelectedRequest(request);
      setShowEventsModal(true);
    } catch (err) {
      console.error('Error fetching events:', err);
      alert('Failed to load audit trail');
    }
  };

  // Upload modal handlers
  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadSigners([{ name: '', email: '', type: 'client' }]);
    setUploadEmailSubject('');
    setUploadEmailMessage('');
  };

  const handleAddSigner = () => {
    setUploadSigners([...uploadSigners, { name: '', email: '', type: 'client' }]);
  };

  const handleRemoveSigner = (index) => {
    if (uploadSigners.length > 1) {
      setUploadSigners(uploadSigners.filter((_, i) => i !== index));
    }
  };

  const handleSignerChange = (index, field, value) => {
    const updated = [...uploadSigners];
    updated[index][field] = value;
    setUploadSigners(updated);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      alert('Please select a PDF file');
      return;
    }

    if (!uploadTitle.trim()) {
      alert('Please enter a document title');
      return;
    }

    const validSigners = uploadSigners.filter(s => s.name.trim() && s.email.trim());
    if (validSigners.length === 0) {
      alert('Please add at least one signer with name and email');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_title', uploadTitle);
      formData.append('signers', JSON.stringify(validSigners.map((s, idx) => ({
        signer_name: s.name,
        signer_email: s.email,
        signer_type: s.type,
        signing_order: idx + 1
      }))));
      if (uploadEmailSubject) formData.append('email_subject', uploadEmailSubject);
      if (uploadEmailMessage) formData.append('email_message', uploadEmailMessage);

      await uploadDocumentForSigning(formData);

      alert('Document uploaded successfully! You can now send it for signing.');
      setShowUploadModal(false);
      resetUploadForm();
      fetchRequests();
    } catch (err) {
      console.error('Error uploading document:', err);
      alert(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Signature Requests</h2>
          <p className="text-sm text-virgo-teal-400 mt-1">Manage electronic signature requests</p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="partially_signed">Partially Signed</option>
            <option value="completed">Completed</option>
            <option value="declined">Declined</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg shadow-cyan-500/20"
          >
            <Upload className="w-4 h-4" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 overflow-hidden backdrop-blur-sm">
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-virgo-teal-500 mx-auto mb-3" />
            <p className="text-virgo-teal-400">No signature requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-virgo-navy-600">
              <thead className="bg-virgo-navy-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Signers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-virgo-navy-600">
                {requests.map((request) => {
                  const status = STATUS_COLORS[request.status] || STATUS_COLORS.draft;
                  const StatusIcon = status.icon;
                  const signedCount = request.signers?.filter(s => s.status === 'signed').length || 0;
                  const totalSigners = request.signers?.length || 0;

                  return (
                    <tr key={request.id} className="hover:bg-virgo-navy-600/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-white">
                              {request.document_title}
                            </div>
                            <div className="text-xs text-virgo-teal-500">
                              {request.document_type.replace('_', ' ')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${status.bg} ${status.text} ${status.border}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {request.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {signedCount} / {totalSigners} signed
                        </div>
                        <div className="w-full bg-virgo-navy-600 rounded-full h-1.5 mt-1">
                          <div
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full"
                            style={{ width: `${(signedCount / totalSigners) * 100}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                        {formatDate(request.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                        {formatDate(request.expires_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {request.status === 'draft' && (
                            <button
                              onClick={() => handleSend(request.id)}
                              className="text-blue-400 hover:text-blue-300 transition-colors"
                              title="Send"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            onClick={() => handleViewEvents(request)}
                            className="text-virgo-teal-400 hover:text-white transition-colors"
                            title="View Audit Trail"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {!['completed', 'cancelled'].includes(request.status) && (
                            <button
                              onClick={() => handleCancel(request.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Events Modal */}
      {showEventsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Audit Trail</h3>
                <p className="text-sm text-virgo-teal-400 mt-1">{selectedRequest?.document_title}</p>
              </div>
              <button
                onClick={() => setShowEventsModal(false)}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {events.length === 0 ? (
                <p className="text-center text-virgo-teal-400 py-8">No events recorded</p>
              ) : (
                <div className="space-y-4">
                  {events.map((event) => (
                    <div key={event.id} className="flex gap-4 p-4 bg-virgo-navy-800/50 rounded-lg border border-virgo-teal-700/30">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center border border-cyan-500/30">
                          <Calendar className="w-5 h-5 text-cyan-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-white">{event.event_type.replace('_', ' ')}</p>
                            <p className="text-sm text-virgo-teal-300 mt-1">{event.event_description}</p>
                          </div>
                          <span className="text-xs text-virgo-teal-500 whitespace-nowrap ml-4">
                            {formatDate(event.created_at)}
                          </span>
                        </div>
                        {event.ip_address && (
                          <div className="mt-2 text-xs text-virgo-teal-500">
                            IP: {event.ip_address}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-virgo-navy-600 flex justify-end">
              <button
                onClick={() => setShowEventsModal(false)}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Upload Document for Signing</h3>
                <p className="text-sm text-virgo-teal-400 mt-1">Upload a PDF and add signers</p>
              </div>
              <button
                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  PDF Document *
                </label>
                <div className="border-2 border-dashed border-virgo-teal-700/50 rounded-lg p-6 text-center hover:border-cyan-500/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-virgo-teal-500 mx-auto mb-2" />
                    {uploadFile ? (
                      <p className="text-white font-medium">{uploadFile.name}</p>
                    ) : (
                      <>
                        <p className="text-virgo-teal-300">Click to upload PDF</p>
                        <p className="text-xs text-virgo-teal-500 mt-1">PDF files only</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Document Title */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Document Title *
                </label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="e.g., Service Agreement"
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  required
                />
              </div>

              {/* Signers */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-virgo-teal-300">
                    Signers *
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSigner}
                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Add Signer
                  </button>
                </div>
                <div className="mb-2">
                  <ContactPicker
                    placeholder="Add signer from contacts…"
                    allowRawEmail
                    excludeEmails={uploadSigners.map(s => s.email).filter(Boolean)}
                    onSelect={(person) => setUploadSigners(prev => {
                      const row = { name: person.name || '', email: person.email || '', type: 'client' };
                      const emptyIdx = prev.findIndex(s => !s.name && !s.email);
                      if (emptyIdx >= 0) return prev.map((s, i) => (i === emptyIdx ? row : s));
                      return [...prev, row];
                    })}
                  />
                </div>
                <div className="space-y-3">
                  {uploadSigners.map((signer, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={signer.name}
                          onChange={(e) => handleSignerChange(index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="email"
                          value={signer.email}
                          onChange={(e) => handleSignerChange(index, 'email', e.target.value)}
                          placeholder="Email"
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>
                      <div className="w-28">
                        <select
                          value={signer.type}
                          onChange={(e) => handleSignerChange(index, 'type', e.target.value)}
                          className="w-full px-2 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        >
                          <option value="client">Client</option>
                          <option value="attorney">Attorney</option>
                          <option value="witness">Witness</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      {uploadSigners.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSigner(index)}
                          className="p-2 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Email Subject */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Email Subject (optional)
                </label>
                <input
                  type="text"
                  value={uploadEmailSubject}
                  onChange={(e) => setUploadEmailSubject(e.target.value)}
                  placeholder="Custom email subject for signers"
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              {/* Email Message */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Email Message (optional)
                </label>
                <textarea
                  value={uploadEmailMessage}
                  onChange={(e) => setUploadEmailMessage(e.target.value)}
                  placeholder="Custom message to include in the signing email"
                  rows={3}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
                />
              </div>
            </form>

            <div className="px-6 py-4 border-t border-virgo-navy-600 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureRequestsList;
